/**
 * Wallet & Analytics API endpoints
 * Solana balance, holders, Helius, Birdeye, DexScreener, Cielo, Jupiter, Security
 */

import { request, ApiError } from './core';

// ============================================
// HELIUS (Solana Data)
// ============================================

export async function getTokenHolders(mintAddress, limit = 100) {
  return request(`/api/helius/holders/${mintAddress}?limit=${limit}`);
}

export async function getTokenMetadata(mintAddress) {
  return request(`/api/helius/token/${mintAddress}`);
}

// ============================================
// BIRDEYE (Price & Analytics)
// ============================================

export async function getTokenPrice(mintAddress) {
  return request(`/api/birdeye/price/${mintAddress}`);
}

export async function getTokenOverview(mintAddress) {
  return request(`/api/birdeye/overview/${mintAddress}`);
}

// ============================================
// DEXSCREENER (via backend proxy)
// ============================================

export async function getDexScreenerToken(mintAddress) {
  try {
    return await request(`/api/dexscreener/token/${mintAddress}`);
  } catch {
    throw new ApiError('Failed to fetch from DexScreener', 'NETWORK_ERROR', 0);
  }
}

export async function searchDexScreener(query) {
  try {
    const params = new URLSearchParams({ q: query });
    return await request(`/api/dexscreener/search?${params}`);
  } catch {
    throw new ApiError('Failed to search DexScreener', 'NETWORK_ERROR', 0);
  }
}

export async function getNewTokens() {
  try {
    const data = await request('/api/dexscreener/new-tokens');
    const tokens = (data.tokens || []).filter(t => t.chainId === 'solana').slice(0, 50);
    return { tokens };
  } catch {
    throw new ApiError('Failed to fetch new tokens', 'NETWORK_ERROR', 0);
  }
}

// ============================================
// SOLANA RPC (via backend proxy)
// ============================================

export async function getSolanaBalance(address, network = 'mainnet') {
  try {
    const data = await request(`/api/solana/balance/${address}?network=${network}`);
    if (data.error) {
      throw new ApiError(data.error, 'SERVICE_UNAVAILABLE', data.status || 503);
    }
    return { balance: data.balance || 0, network: data.network };
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError('Failed to get balance', 'NETWORK_ERROR', 0);
  }
}

export async function rpcCall(method, params = []) {
  return request('/api/solana/rpc', {
    method: 'POST',
    body: JSON.stringify({ method, params })
  });
}

export async function getRecentPriorityFees() {
  return request('/api/solana/priority-fees');
}

// ============================================
// WALLET ANALYTICS
// ============================================

export async function getWalletTransactions(walletAddress, limit = 10) {
  return request(`/api/analytics/wallet/${walletAddress}/transactions?limit=${limit}`);
}

// ============================================
// CIELO (Wallet Tracking - Free)
// ============================================

export async function getCieloWallet(address) {
  return request(`/api/cielo/wallet/${address}`);
}

export async function getCieloTransactions(address, limit = 50) {
  return request(`/api/cielo/wallet/${address}/transactions?limit=${limit}`);
}

export async function getCieloFeed(limit = 50, type = null) {
  const params = new URLSearchParams({ limit });
  if (type) params.append('type', type);
  return request(`/api/cielo/feed?${params}`);
}

export async function getCieloTrendingWallets(limit = 20) {
  return request(`/api/cielo/trending-wallets?limit=${limit}`);
}

// ============================================
// JUPITER (Prices - Free)
// ============================================

export async function getJupiterPrice(mintAddresses) {
  const ids = Array.isArray(mintAddresses) ? mintAddresses.join(',') : mintAddresses;
  return request(`/api/jupiter/price?ids=${ids}`);
}

export async function getJupiterQuote(inputMint, outputMint, amount, slippageBps = 50) {
  return request(`/api/jupiter/quote?inputMint=${inputMint}&outputMint=${outputMint}&amount=${amount}&slippageBps=${slippageBps}`);
}

// ============================================
// GECKOTERMINAL (Free - via backend)
// ============================================

export async function getGeckoTrending(page = 1) {
  return request(`/api/geckoterminal/trending?page=${page}`);
}

export async function getGeckoNewPools(page = 1) {
  return request(`/api/geckoterminal/new-pools?page=${page}`);
}

export async function getGeckoPool(address) {
  return request(`/api/geckoterminal/pool/${address}`);
}

export async function getGeckoToken(address) {
  return request(`/api/geckoterminal/token/${address}`);
}

export async function getGeckoOHLCV(poolAddress, timeframe = 'hour', limit = 100) {
  return request(`/api/geckoterminal/ohlcv/${poolAddress}?timeframe=${timeframe}&limit=${limit}`);
}

// ============================================
// SECURITY (GoPlus + Token Sniffer - Free)
// ============================================

export async function getTokenSecurity(address) {
  return request(`/api/security/token/${address}`);
}

export async function getGoPlusSecurity(address) {
  return request(`/api/security/goplus/${address}`);
}

export async function batchSecurityCheck(addresses) {
  return request('/api/security/batch', {
    method: 'POST',
    body: JSON.stringify({ addresses })
  });
}

// ============================================
// TOKEN HEALTH
// ============================================

export async function getTokenHealth(mintAddress) {
  return request(`/api/tokenhealth/${mintAddress}`);
}

export async function compareTokenHealth(mintAddresses) {
  return request('/api/tokenhealth/compare', {
    method: 'POST',
    body: JSON.stringify({ mintAddresses })
  });
}

export async function getTokenHealthHistory(mintAddress) {
  return request(`/api/tokenhealth/${mintAddress}/history`);
}

// ============================================
// GAS/PRIORITY FEE TRACKER
// ============================================

export async function getGasRecommendations(network = 'mainnet') {
  return request(`/api/gas/recommendations?network=${network}`);
}

export async function getGasHistory(network = 'mainnet', limit = 50) {
  return request(`/api/gas/history?network=${network}&limit=${limit}`);
}

export async function calculateOptimalGas({ network, urgency, transactionType }) {
  return request('/api/gas/calculate', {
    method: 'POST',
    body: JSON.stringify({ network, urgency, transactionType })
  });
}

export async function getNetworkStatus(network = 'mainnet') {
  return request(`/api/gas/network?network=${network}`);
}
